import { ComponentProps } from "react";
import { isFinite, keyBy } from "lodash";
import type { DesignationDistance } from "react-migration/domains/constraints/designation/hooks/useNearestDesignationQuery";
import { SkeletonLoading } from "react-migration/components/SkeletonLoading";
import { UserUnitLength } from "react-migration/components/UserUnitLength";
import { Insight } from "react-migration/components/Insight/Insight";
import { SectionWithHeaderTooltip } from "../common/SectionWithHeaderTooltip";
import {
  BUS_STOP_CATEGORY,
  MAX_TRANSPORT_RADIUS_METERS,
  SUBWAY_STATION_CATEGORY,
  TRAIN_STATION_CATEGORY,
} from "./constants";

interface TransportSectionProps {
  distances?: DesignationDistance[];
  loading: boolean;
}

export function TransportSection({ distances, loading }: TransportSectionProps) {
  const distancesByCategory = keyBy(distances, "designation.sub_category_id");

  return (
    <SectionWithHeaderTooltip title="Proximity to transport hubs" tooltipText="Distance measured from site boundary">
      {loading ? (
        <div className="relative p-2 h-[102px]">
          <SkeletonLoading cols={3} rows={1} showPadding={false} bgClassName="bg-transparent" />
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          <TransportInsight
            designationDistance={distancesByCategory[SUBWAY_STATION_CATEGORY]}
            title="Tube station"
            data-testid="tube-station-insight"
          />
          <TransportInsight
            designationDistance={distancesByCategory[TRAIN_STATION_CATEGORY]}
            title="Train station"
            data-testid="train-station-insight"
          />
          <TransportInsight
            designationDistance={distancesByCategory[BUS_STOP_CATEGORY]}
            title="Bus stop"
            data-testid="bus-stop-insight"
          />
        </div>
      )}
    </SectionWithHeaderTooltip>
  );
}

type TransportInsightProps = { designationDistance: DesignationDistance } & Pick<
  ComponentProps<typeof Insight.Wrapper>,
  "title" | "tooltip" | "data-testid"
>;

function TransportInsight({ designationDistance, ...props }: TransportInsightProps) {
  const beyondMaxDistance =
    !isFinite(designationDistance?.distanceMeters) || designationDistance.distanceMeters > MAX_TRANSPORT_RADIUS_METERS;
  const distanceMeters = designationDistance?.distanceMeters ?? MAX_TRANSPORT_RADIUS_METERS;

  return (
    <Insight.Wrapper {...props}>
      <Insight.Body>
        <UserUnitLength lengthMeters={distanceMeters} prefix={beyondMaxDistance ? ">" : ""} />
      </Insight.Body>
      <Insight.Footer>{designationDistance?.designation?.display_name ?? "None nearby"}</Insight.Footer>
    </Insight.Wrapper>
  );
}
